import {
  AiReportCellKind,
  AiReportData,
  AiReportTable,
} from "../../../slices/AiSlice";

/**
 * "Ask about this report" (owner 2026-10-25): each report describes what it shows as
 * tables of plain cells; the server answers only from them and points at rows by key,
 * so the panel shows the report's own numbers (never numbers written by the AI).
 */

export const ASK_MAX_ROWS = 400;

export type AskColumn<T> = {
  key: string;
  label: string;
  kind: AiReportCellKind;
  value: (row: T) => string | number | null | undefined;
};

const cell = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number")
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
  const text = String(value).trim();
  return text ? text.slice(0, 200) : null;
};

/** One table of the report: columns + rows (key and optional in-app link per row). */
export const askTable = <T>(
  name: string,
  rows: T[],
  columns: AskColumn<T>[],
  key: (row: T, index: number) => string,
  link?: (row: T) => string | null | undefined,
): AiReportTable => ({
  name,
  columns: columns.map(({ key: k, label, kind }) => ({ key: k, label, kind })),
  rows: rows.map((row, index) => {
    const l = link?.(row);
    return {
      key: String(key(row, index)).slice(0, 100),
      cells: Object.fromEntries(
        columns.map((c) => [c.key, cell(c.value(row))]),
      ),
      ...(l && l.startsWith("/") && !l.startsWith("//") ? { link: l } : {}),
    };
  }),
});

/** Keeps at most ASK_MAX_ROWS rows over all tables (first tables first). */
export const capReportData = (data: AiReportData): AiReportData => {
  let left = ASK_MAX_ROWS;
  return {
    ...data,
    tables: data.tables
      .filter((t) => t.rows.length)
      .map((t) => {
        const rows = t.rows.slice(0, Math.max(0, left));
        left -= rows.length;
        return { ...t, rows };
      })
      .filter((t) => t.rows.length),
  };
};

/** "Date: This year · Team member: 2 picked" from the report's filters. */
export const filtersText = (parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(" · ");

export type ReportFocus = { table: string; keys: string[] } | null;

/** Rows of `table` limited to the AI answer's rows (when the person clicked "Show only"). */
export const focusRows = <T>(
  focus: ReportFocus,
  table: string,
  rows: T[],
  keyOf: (row: T, index: number) => string,
): T[] => {
  if (!focus || focus.table !== table) return rows;
  const keys = new Set(focus.keys);
  return rows.filter((row, i) => keys.has(String(keyOf(row, i))));
};
